import dayjs from 'dayjs';
import stringToDate from './stringToDate';
import mixpanelTracker from './mixpanelTracker';

export const MOBILITY_BASE_URL = 'https://api.mobility-cdn.com/hotels/v1/lbh';
export const DEFAULT_MOBILITY_AID = 'MPP';
export const MOBILITY_LEAVE_BEHIND_CONTEXT = 'search_widget_leave_behind';

const ISO_DATE_FORMAT = 'YYYY-MM-DD';

export const normalizeMobilityAid = (aid) => {
  if (typeof aid !== 'string') return DEFAULT_MOBILITY_AID;
  const trimmed = aid.trim();
  return trimmed || DEFAULT_MOBILITY_AID;
};

export const resolvePlaceName = (location) => {
  if (!location) return '';
  return (
    location.cityName ||
    location.city_name ||
    location.city ||
    location.name ||
    ''
  );
};

export const toDateOnly = (value, dateFormat) => {
  if (!value || typeof value !== 'string') return '';

  const isoMatch = value.match(/^\d{4}-\d{2}-\d{2}/);
  if (isoMatch) return isoMatch[0];

  const parsed = stringToDate(value, dateFormat);
  if (!parsed || Number.isNaN(parsed.getTime())) return '';

  return dayjs(parsed).format(ISO_DATE_FORMAT);
};

const addOneDayToDateOnly = (dateOnly) => {
  if (!dateOnly) return '';
  const date = dayjs(`${dateOnly}T12:00:00`);
  if (!date.isValid()) return '';
  return date.add(1, 'day').format(ISO_DATE_FORMAT);
};

export const getMobilityReturnDate = ({ departureDate, returnDate, tripType, dateFormat }) => {
  const departureDateOnly = toDateOnly(departureDate, dateFormat);

  if (tripType === 'round' && returnDate) {
    let returnDateOnly = toDateOnly(returnDate, dateFormat);

    if (departureDateOnly && returnDateOnly && returnDateOnly === departureDateOnly) {
      returnDateOnly = addOneDayToDateOnly(departureDateOnly);
    }

    return returnDateOnly;
  }

  if (departureDateOnly) {
    return addOneDayToDateOnly(departureDateOnly);
  }

  return '';
};

export const buildMobilityUrlFromSearch = ({
  origin,
  destination,
  departureDate,
  returnDate,
  tripType,
  dateFormat,
  aid = DEFAULT_MOBILITY_AID,
}) => {
  const destinationName = resolvePlaceName(destination);
  const originName = resolvePlaceName(origin);
  const departureDateOnly = toDateOnly(departureDate, dateFormat);
  const returnDateOnly = getMobilityReturnDate({
    departureDate,
    returnDate,
    tripType,
    dateFormat,
  });

  if (!destinationName) {
    console.warn('[Mobility leave-behind] buildMobilityUrlFromSearch: no destination name', {
      destination,
      origin,
      tripType,
    });
    return null;
  }

  const params = new URLSearchParams({
    aid: normalizeMobilityAid(aid),
    destination: destinationName,
  });

  if (originName) params.set('origin', originName);
  if (departureDateOnly) params.set('departure_date', departureDateOnly);
  if (returnDateOnly) params.set('return_date', returnDateOnly);

  const mobilityUrl = `${MOBILITY_BASE_URL}?${params.toString()}`;

  return mobilityUrl;
};

export const shouldOpenMobilityLeaveBehind = ({ mobilityEnabled, mobilitySelected }) =>
  Boolean(mobilityEnabled && mobilitySelected);

/**
 * Navigates the top browsing context when allowed (e.g. from an iframe embed).
 * @param {string} url
 * @returns {boolean}
 */
export const navigateTopWindow = (url) => {
  try {
    window.top.location.href = url;
    return true;
  } catch (error) {
    console.warn('[Mobility leave-behind] window.top navigation blocked', {
      url,
      error,
    });
  }

  try {
    if (window.parent && window.parent !== window) {
      window.parent.location.href = url;
      return true;
    }
  } catch (error) {
    console.warn('[Mobility leave-behind] window.parent navigation blocked', {
      url,
      error,
    });
  }

  window.location.href = url;
  return false;
};

/**
 * Opens bus results in a new tab (focused) and navigates the current tab to Mobility.
 * Falls back to bus-only redirect when popup is blocked.
 * @param {object} params
 * @param {string} params.busUrl
 * @param {string} params.mobilityUrl
 * @returns {boolean} Whether leave-behind dual navigation was executed.
 */
export const openMobilityLeaveBehind = ({ busUrl, mobilityUrl }) => {
  let topHref;
  try {
    topHref = window.top?.location?.href;
  } catch (error) {
    topHref = `[blocked: ${error?.message || 'cross-origin'}]`;
  }

  const hostContext = {
    hostHref: window.location.href,
    topHref,
    isTopWindow: window.top === window,
    isIframe: window.top !== window,
  };

  console.info('[Mobility leave-behind] openMobilityLeaveBehind:start', {
    busUrl,
    mobilityUrl,
    hostContext,
  });

  // Do not pass noopener/noreferrer here: modern browsers return null even when the
  // tab opens successfully, which would falsely trigger the popup-blocked fallback.
  const busTab = window.open(busUrl, '_blank');
  if (!busTab) {
    console.warn('[Mobility leave-behind] popup blocked — fallback to bus-only in current tab', {
      busUrl,
    });
    navigateTopWindow(busUrl);
    return false;
  }

  busTab.opener = null;

  const navigatedToMobility = navigateTopWindow(mobilityUrl);
  if (navigatedToMobility) {
    console.info('[Mobility leave-behind] top window navigated to Mobility', { mobilityUrl });
  } else {
    console.warn('[Mobility leave-behind] fell back to iframe navigation for Mobility', {
      mobilityUrl,
      hostContext,
    });
  }

  return true;
};

/**
 * Tracks Mobility hotel search intent from the search widget leave-behind flow.
 * @param {object} params
 * @param {string} [params.token] - Purchase token (not used in search widget).
 * @param {string} [params.aid] - Mobility affiliate id.
 * @param {string} [params.context] - UI context where the event happened.
 */
export function mobilitySearchHotelsClickTracker({
  token,
  aid,
  context = MOBILITY_LEAVE_BEHIND_CONTEXT,
}) {
  mixpanelTracker.trackEvent('Mobility Search Hotels Clicked', {
    ...(token && { 'Purchase Token': token }),
    Aid: normalizeMobilityAid(aid),
    Context: context,
  });
}
